import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { Info, Link, Unlink } from 'lucide-react';
import { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import ModelDetails from '~/components/ModelDetails';
import ImageProperties from '~/components/Properties/ImageProperties';
import PropertiesTooltip from '~/components/Properties/PropertiesTooltip';
import SelectableListItem from '~/components/SelectableListItem/SelectableListItem';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { Image3D } from '~/domain/patient/image3d';
import { RigidRegistration } from '~/domain/patient/rigidRegistration';
import { showSensitiveInfoState } from '~/state/atoms/viewerAtoms';
import { formatSeriesDate } from '~/utils/date';
import { obliquityAngleDeg } from '~/utils/obliquity';
import { transform3DImageData } from '~/utils/transforms/imageDataTransforms';

const modalityChipSx = {
    fontSize: 10,
    maxHeight: '12px',
    px: 0,
};

const LINK_ICON_SIZE = 10;

function UnlinkButton({ onUnlink }: { onUnlink: () => void }) {
    const intl = useIntl();
    const [hovered, setHovered] = useState(false);
    return (
        <Tooltip title={intl.formatMessage({ id: 'common.unload' })} arrow>
            <Box
                component="span"
                onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onUnlink();
                }}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                sx={{
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: hovered ? 'error.main' : 'gray',
                    transition: 'color 0.15s ease-in-out',
                }}
            >
                {hovered ? (
                    <Unlink size={LINK_ICON_SIZE} />
                ) : (
                    <Link size={LINK_ICON_SIZE} />
                )}
            </Box>
        </Tooltip>
    );
}

interface ImageItemProps {
    image: Image3D;
    isSecondary?: boolean;
    hasSharedFrameOfReference?: boolean;
    isSelected?: boolean;
    onSelect?: () => void;
    onUnlink?: () => void;
    registration?: RigidRegistration;
    onDelete?: () => void;
}

export default function ImageItem({
    image,
    isSecondary,
    hasSharedFrameOfReference,
    isSelected,
    onSelect,
    onUnlink,
    registration,
    onDelete,
}: ImageItemProps) {
    const intl = useIntl();
    const showSensitiveInfo = useRecoilValue(showSensitiveInfoState);

    const metadata = useMemo(() => transform3DImageData(image), [image]);

    const isOblique = !!image.firstSlice.tilted;
    const obliqueAngle = useMemo(
        () =>
            isOblique
                ? obliquityAngleDeg(image.firstSlice.imageOrientation).toFixed(
                      1
                  )
                : null,
        [isOblique, image.firstSlice.imageOrientation]
    );

    const description =
        metadata.series.description ||
        intl.formatMessage({ id: 'unknown.series' });

    const sliceThicknessCm = (metadata.pixelSpacing[2] / 10).toFixed(2);
    const content = (
        <PropertiesTooltip
            title={
                <ImageProperties
                    image={image}
                    hasSharedFrameOfReference={hasSharedFrameOfReference}
                    onDelete={onDelete}
                />
            }
        >
            <Box display="grid">
                <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    width="100%"
                    overflow="hidden"
                >
                    {isSecondary && (
                        <Box
                            display="inline-flex"
                            alignItems="center"
                            justifyContent="center"
                            flexShrink={0}
                        >
                            {onUnlink ? (
                                <UnlinkButton onUnlink={onUnlink} />
                            ) : (
                                <Link size={LINK_ICON_SIZE} color="gray" />
                            )}
                        </Box>
                    )}
                    <Chip
                        label={metadata.modality}
                        size="small"
                        sx={{
                            mr: -0.75,
                            ...modalityChipSx,
                        }}
                    />
                    <Typography
                        fontSize={12}
                        noWrap
                        flex={1}
                        minWidth={0}
                        title={showSensitiveInfo ? description : undefined}
                        color={
                            isSelected || isSelected === undefined
                                ? 'text.primary'
                                : 'text.secondary'
                        }
                    >
                        <SensitiveInfoTypography>
                            {description}
                        </SensitiveInfoTypography>
                    </Typography>
                    <Box
                        display="flex"
                        alignItems="center"
                        gap={0.5}
                        flexShrink={0}
                    >
                        {metadata.hasInterpolatedSlices && (
                            <Tooltip
                                title={
                                    <FormattedMessage
                                        id="image.interpolatedSlices.tooltip"
                                        values={{
                                            count: metadata.interpolatedSliceCount,
                                            total: metadata.length,
                                        }}
                                    />
                                }
                                arrow
                                placement="top"
                                slotProps={{
                                    popper: {
                                        sx: {
                                            zIndex: (theme) =>
                                                theme.zIndex.tooltip + 1,
                                        },
                                    },
                                }}
                            >
                                <Box
                                    component="span"
                                    display="inline-flex"
                                    alignItems="center"
                                    role="img"
                                    tabIndex={0}
                                    sx={{
                                        color: 'warning.main',
                                        cursor: 'help',
                                        outline: 'none',
                                        '&:focus-visible': {
                                            outline: '1px solid',
                                            outlineColor: 'warning.main',
                                            outlineOffset: 2,
                                            borderRadius: '50%',
                                        },
                                    }}
                                    aria-label={intl.formatMessage(
                                        {
                                            id: 'image.interpolatedSlices.tooltip',
                                        },
                                        {
                                            count: metadata.interpolatedSliceCount,
                                            total: metadata.length,
                                        }
                                    )}
                                >
                                    <Info size={LINK_ICON_SIZE} />
                                </Box>
                            </Tooltip>
                        )}
                        {hasSharedFrameOfReference && (
                            <Chip
                                label="FOR"
                                size="small"
                                color="primary"
                                sx={modalityChipSx}
                            />
                        )}
                        {obliqueAngle && (
                            <Tooltip
                                title={intl.formatMessage(
                                    { id: 'image.oblique.tooltip' },
                                    { angle: obliqueAngle }
                                )}
                                arrow
                                placement="top"
                                slotProps={{
                                    popper: {
                                        sx: {
                                            zIndex: (theme) =>
                                                theme.zIndex.tooltip + 1,
                                        },
                                    },
                                }}
                            >
                                <Chip
                                    label={intl.formatMessage(
                                        { id: 'image.oblique.chip' },
                                        { angle: obliqueAngle }
                                    )}
                                    size="small"
                                    color="warning"
                                    sx={modalityChipSx}
                                />
                            </Tooltip>
                        )}

                        <Typography
                            fontSize={10}
                            color="text.secondary"
                            whiteSpace="nowrap"
                        >
                            {intl.formatMessage(
                                { id: 'image.sliceCount' },
                                { count: metadata.length }
                            )}
                        </Typography>
                    </Box>
                </Box>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={2}
                    width="100%"
                    overflow="hidden"
                >
                    <Typography
                        fontSize={10}
                        color="text.secondary"
                        whiteSpace="nowrap"
                        flexShrink={0}
                    >
                        <SensitiveInfoTypography>
                            {formatSeriesDate(metadata.series.date) ||
                                'Unknown date'}
                        </SensitiveInfoTypography>
                    </Typography>
                    <Typography fontSize={10} color="text.secondary" noWrap>
                        {intl.formatMessage(
                            { id: 'image.sliceThickness' },
                            { value: sliceThicknessCm }
                        )}
                    </Typography>
                </Box>
                {registration && (
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        gap={1}
                    >
                        <Typography fontSize={10} color="text.secondary" noWrap>
                            {intl.formatMessage({
                                id: 'image.rigidRegistration',
                            })}
                        </Typography>
                        <Typography
                            fontSize={10}
                            color="text.secondary"
                            noWrap
                            title={
                                showSensitiveInfo
                                    ? registration.contentDescription ||
                                      registration.contentLabel
                                    : undefined
                            }
                        >
                            <SensitiveInfoTypography>
                                {registration.contentLabel ||
                                    registration.contentDescription ||
                                    registration.sopInstanceUid}
                            </SensitiveInfoTypography>
                        </Typography>
                    </Box>
                )}
                <ModelDetails
                    fullWidth
                    generationModel={metadata.generationModel}
                />
            </Box>
        </PropertiesTooltip>
    );

    if (onSelect === undefined && isSelected === undefined) {
        return content;
    }

    return (
        <SelectableListItem isSelected={isSelected} onClick={onSelect}>
            {content}
        </SelectableListItem>
    );
}
